import { defineStore } from "pinia";
import { useToolsStore } from "./tools";
import { useMainStore } from "./main";
import { useConfigStore } from "./config";
import { useAlertStore } from "./alert";
import * as cornerstone3D from "@cornerstonejs/core";
import cornerstoneDICOMImageLoader from "@cornerstonejs/dicom-image-loader";
import axios from "axios";
import { utilities as csUtils } from "@cornerstonejs/core";
import { useEventsStore } from "./events";
import html2canvas from "html2canvas-pro";

import {
  formatDicomDate,
  formatDicomTime,
  GetAgeFromDicomDate,
} from "../composables/DicomUtils";

export const useUIStore = defineStore("ui", {
  state: () => ({
    // Modals
    meataDataModalOpen:    false,
    reportModalOpen:       false,
    settingsModalActive:   false,
    aboutModalActive:      false,
    // Layout
    expanded:              false,
    isMobile:              false,
    showLayoutChanger:     false,
    showPatientInfo:       true,
    defaultLayouts:        [],
    centerName:            "",
    screenWidth:           1920,
    screenHeight:          1080,
    // Thumbnail
    thumbnailVertical:     true,
    thumbnailLeft:         true,
    thumbnailBottom:       true,
    thumbnailHSize:        "",
    thumbnailVSize:        "",
    firstLayoutLoad:       true,
    resize:                false,
  }),

  getters: {
    screenSize: (state) => {
      if (state.screenWidth < 1000)  return "small";
      if (state.screenWidth < 1675)  return "medium";
      return "large";
    },
    orientation: (state) => {
      return state.screenWidth < state.screenHeight ? "portrait" : "landscape";
    },
  },

  actions: {
    renderImage(element) {
      const mainStore   = useMainStore();
      const configStore = useConfigStore();
      const events      = useEventsStore();
      const toolStore   = useToolsStore();

      if (!(element instanceof HTMLElement)) return;
      if (!element.parentElement) return;
      if (!element.attributes["data-rendering-engine-uid"]) return;

      const canvas          = element;
      const renderingEngine = cornerstone3D.getRenderingEngine(
        element.attributes["data-rendering-engine-uid"].value
      );
      const viewport = renderingEngine.getViewport(
        element.attributes["data-viewport-uid"].value
      );

      const metadata = cornerstoneDICOMImageLoader.wadors.metaDataManager.get(
        viewport.imageIds[viewport.getTargetImageIdIndex()]
      );
      const studyUID  = metadata["0020000D"].Value[0];
      const seriesUID = metadata["0020000E"].Value[0];

      toolStore.AddOrientationMarkersToViewport(viewport);

      // Bottom-left: image index counter + download button
      const bottomLeft      = canvas.parentElement.parentElement.children[1];
      const currentImageIndex = viewport.getCurrentImageIdIndex() + 1;
      const totalImages     = viewport.imageIds.length;
      bottomLeft.innerHTML  = `I: ${currentImageIndex}/${totalImages}`;

      const downloadButton = document.createElement("button");
      downloadButton.style.border     = "none";
      downloadButton.style.background = "transparent";
      downloadButton.style.cursor     = "pointer";
      downloadButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" height="32" viewBox="0 0 960 960" width="32" fill="#68BCCA" style="margin-bottom: -4px;">
          <path d="M480 773.538l221.769-221.769-56.231-56.23-125.538 125.538V280h-80v340.077L314.462 495.538l-56.23 56.231L480 773.538Zm-280 122.462V880h560v16h-560Zm0 16V880v32Z"/>
        </svg>
      `;
      downloadButton.onclick = () => {
        canvas.style.transition = "opacity 0.2s ease-out";
        canvas.style.opacity    = "0.2";
        setTimeout(() => {
          canvas.style.opacity = "1";
          const link      = document.createElement("a");
          link.download   = `image_${currentImageIndex}.png`;
          html2canvas(canvas).then((generatedCanvas) => {
            link.href = generatedCanvas.toDataURL("image/png");
            link.click();
          });
        }, 200);
      };
      bottomLeft.appendChild(downloadButton);

      // Bottom-right: zoom + WW/WL
      if (viewport.getProperties().voiRange) {
        const zoom       = viewport.getZoom().toFixed(4);
        const bottomRight = canvas.parentElement.parentElement.children[2];
        const { lower, upper } = viewport.getProperties().voiRange;
        const wwwc = csUtils.windowLevel.toWindowLevel(lower, upper);
        bottomRight.innerHTML = `Zoom: ${zoom}<br>W: ${parseInt(wwwc.windowWidth)} L: ${parseInt(wwwc.windowCenter)}`;
      }

      // Restore saved zoom if applicable
      const vpIndex = events.viewportProperties.findIndex((x) => x.id === viewport.id);
      if (vpIndex !== -1 && viewport.getZoom() === 1) {
        viewport.setZoom(events.viewportProperties[vpIndex].zoomFactor);
        viewport.render();
      }

      // Top-left: patient info
      const topLeft = canvas.parentElement.parentElement.children[3];
      const study   = mainStore.studiesLoaded.find((x) => x.studyUID === studyUID);
      const patientName = study.patientName;
      topLeft.innerHTML = "";

      if (configStore.canvasPatientDetails.name && patientName) {
        topLeft.innerHTML += `${patientName} <br/>`;
      }

      const patientID = mainStore.GetAttribute(metadata["00100020"]);
      if (patientName) {
        topLeft.innerHTML += `ID ${patientID} <br/>`;
      }

      if (configStore.canvasPatientDetails.age) {
        const DOB = mainStore.studiesLoaded[mainStore.selectedStudy - 1].DOB;
        if (DOB) {
          const age = GetAgeFromDicomDate(DOB);
          if (Number.isInteger(age)) topLeft.innerHTML += `Age ${age} <br/>`;
        }
      }

      if (configStore.canvasPatientDetails.sex) {
        const sex = metadata["00100040"];
        if (sex != null && sex !== "") {
          topLeft.innerHTML += `Sex ${mainStore.GetAttribute(sex)} <br/>`;
        }
      }

      const DOB = mainStore.GetAttribute(metadata["00100030"]);
      if (DOB) topLeft.innerHTML += `DOB ${formatDicomDate(DOB)} <br/>`;

      if (study.prior) topLeft.innerHTML += "PRIOR";

      // Top-right: series info
      const topRight       = canvas.parentElement.parentElement.children[4];
      topRight.innerHTML   = "";
      const series         = study.series.find((x) => x.seriesUID === seriesUID);
      const seriesDescription = series.seriesDescription?.slice(0, 25) ?? "";
      const seriesNumber   = series.seriesNumber;

      const modality = mainStore.GetAttribute(metadata["00080060"]);
      if (modality)      topRight.innerHTML += `${modality} <br/>`;
      if (seriesNumber)  topRight.innerHTML += `Series ${seriesNumber} <br/>`;

      const seriesDate = mainStore.GetAttribute(metadata["00080021"]);
      if (seriesDate)    topRight.innerHTML += `${formatDicomDate(seriesDate)} <br/>`;

      const seriesTime = mainStore.GetAttribute(metadata["00080031"]);
      if (seriesTime)    topRight.innerHTML += `${formatDicomTime(seriesTime)} <br/>`;

      if (seriesDescription) topRight.innerHTML += `${seriesDescription} <br/>`;

      if (mainStore.isEchoLayout && metadata["00181088"]) {
        topRight.innerHTML  = `${metadata["00181088"].Value[0]} BPM <br/>`;
        topRight.innerHTML += `${metadata["00082127"].Value[0]} <br/>`;
        topRight.innerHTML += `${metadata["00082120"].Value[0]} <br/>`;
      }
    },

    ImageRenderedEventForElement(element) {
      this.renderImage(element);
      element.addEventListener(cornerstone3D.EVENTS.IMAGE_RENDERED, (e) => {
        this.renderImage(e.target);
      });
    },

    setCookie(name, value, days) {
      let expires = "";
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie = name + "=" + (value || "") + expires + "; path=/";
    },

    getCookie(name) {
      const nameEQ = name + "=";
      const ca     = document.cookie.split(";");
      for (let c of ca) {
        c = c.trim();
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
      }
      return null;
    },

    eraseCookie(name) {
      document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    },

    SetActiveTool(toolName, mouseButton) {
      const toolStore = useToolsStore();
      toolStore.ActivateTool(toolName, mouseButton);
    },

    SetLayoutRowCol(layout) {
      const alertStore = useAlertStore();
      const mainStore  = useMainStore();

      const canvasElement =
        document.getElementById("canvases") ||
        document.getElementById("canvases-right") ||
        document.getElementById("canvases-bottom") ||
        document.getElementById("canvases-top");
      if (!canvasElement) return;

      if (this.firstLayoutLoad && !mainStore.vrMode && !mainStore.mprMode) {
        this.firstLayoutLoad = false;
      }

      mainStore.totalCanvases = canvasElement.children.length;

      let row = layout.row;
      let col = layout.col;
      const portraitTrue = window.matchMedia("(orientation: portrait)").matches;

      if (portraitTrue) {
        let rowFlag = false, colFlag = false;
        if (row > 5) { row = 5; rowFlag = true; }
        if (col > 3) { col = 3; colFlag = true; }
        if      (rowFlag && colFlag) alertStore.showFailureAlert({ text: "Layout limited to maximum of 3x5 (rowsXcols) due to portrait orientation" });
        else if (rowFlag)            alertStore.showFailureAlert({ text: "Layout rows limited to maximum of 5 due to portrait orientation" });
        else if (colFlag)            alertStore.showFailureAlert({ text: "Layout columns limited to maximum of 3 due to portrait orientation" });
      } else {
        let rowFlag = false, colFlag = false;
        if (row > 4) { row = 4; rowFlag = true; }
        if (col > 8) { col = 8; colFlag = true; }
        if      (rowFlag && colFlag) alertStore.showFailureAlert({ text: "Layout limited to maximum of 4x8 (rowsXcols) due to landscape layout" });
        else if (rowFlag)            alertStore.showFailureAlert({ text: "Layout rows limited to maximum of 4 due to landscape layout" });
        else if (colFlag)            alertStore.showFailureAlert({ text: "Layouts columns limited to maximum of 8 due to landscape layout" });
      }

      const rowscols = `${row}x${col}`;
      if (rowscols !== mainStore.prevLayout && rowscols !== "1x1" && !mainStore.mprMode) {
        mainStore.focusSeries = false;
      }

      mainStore.SetLayout(rowscols);
      canvasElement.style.gridTemplateRows    = `repeat(${row}, auto)`;
      canvasElement.style.gridTemplateColumns = `repeat(${col}, auto)`;
      this.resize = true;
    },

    updatePatientInformationFontSizeForLayout(rows, portrait) {
      const canvasElement =
        document.getElementById("canvases") ||
        document.getElementById("canvases-right") ||
        document.getElementById("canvases-bottom") ||
        document.getElementById("canvases-top");

      let fontSize   = 15;
      let topPercent = 97;
      let PIfontSize = 14;

      if (!portrait) {
        const map = { "2": [95, 13, 13], "3": [93, 12, 12], "4": [90, 11, 11] };
        if (map[rows]) [topPercent, fontSize, PIfontSize] = map[rows];
      } else if (this.thumbnailVertical) {
        const map = { "1": [98, 15, 14], "2": [97, 13, 13], "3": [95, 12, 12], "4": [94, 11, 11], "5": [93, 11, 11] };
        if (map[rows]) [topPercent, fontSize, PIfontSize] = map[rows];
      } else {
        const map = { "1": [97, 15, 14], "2": [96, 13, 13], "3": [94, 12, 12], "4": [93, 11, 11], "5": [90, 11, 11] };
        if (map[rows]) [topPercent, fontSize, PIfontSize] = map[rows];
      }

      for (const canvas of canvasElement?.children ?? []) {
        const [br, bl, tl] = canvas.querySelectorAll(".patient-info");
        const left   = canvas.querySelector(".left-middle");
        const right  = canvas.querySelector(".right-middle");
        const top    = canvas.querySelector(".top-middle");
        const bottom = canvas.querySelector(".bottom-middle");

        if (left && right && top && bottom) {
          for (const el of [left, right, top, bottom]) {
            el.style.fontSize = `${fontSize}px`;
            el.querySelector(".secondary-marker").style.fontSize = `${fontSize - 4}px`;
          }
          top.style.bottom = `${topPercent}%`;
        }
        if (br && bl && tl) {
          for (const el of [br, bl, tl]) el.style.fontSize = `${PIfontSize}px`;
        }
      }
    },

    async UpdateDefaultTools(defaults) {
      const config  = useConfigStore();
      const postURL = `${config.defaultsRoot}/SaveDefaultTools`;

      for (const temp of defaults) {
        const data = new FormData();
        data.append("modality", temp.modality);
        data.append("control",  temp.control);
        data.append("tool",     temp.tool);
        await axios
          .post(postURL, data, { headers: { "Content-Type": "multipart/form-data" } })
          .catch((error) => console.error("Error saving default tool:", error));
      }
    },

    async UpdateDefaultThumbnails() {
      const defaultThumbs = {
        thumbnailHSize:   this.thumbnailHSize,
        thumbnailVSize:   this.thumbnailVSize,
        thumbnailHBottom: this.thumbnailBottom,
        thumbnailVleft:   this.thumbnailLeft,
      };
      localStorage.setItem("defaultThumb", JSON.stringify(defaultThumbs));
    },

    SettingsModalOpen()  { this.settingsModalActive = true;  },
    SettingsModalClose() { this.settingsModalActive = false; },
    AboutModalOpen()     { this.aboutModalActive    = true;  },
    AboutModalClose()    { this.aboutModalActive    = false; },

    // TODO: implement dedicated modal states for Print, Export, BurnCD
    PrintModalOpen()  { this.aboutModalActive = true; },
    ExportModalOpen() { this.aboutModalActive = true; },
    BurnCDModalOpen() { this.aboutModalActive = true; },

    Expand() {
      this.expanded = true;
      document.getElementById("viewer-content").style.gridColumnStart = "1";
      document.getElementById("viewer-content").style.gridColumnEnd   = "5";
      document.getElementById("viewer-container").style.gridTemplateColumns = "160px auto";
      document.getElementById("main-layout").style.gridTemplateRows   = "60px";
      const toolbar = document.getElementById("toolbar");
      if (toolbar) toolbar.style.gridTemplateRows = "60px";
      this.ToggleFullscreenForElement(document.getElementById("app"));
    },

    Collapse(dontToggle) {
      this.expanded = false;
      document.getElementById("viewer-content").style.gridColumnStart = "2";
      document.getElementById("viewer-content").style.gridColumnEnd   = "5";
      document.getElementById("viewer-container").style.gridTemplateColumns = "240px auto";
      document.getElementById("main-layout").style.gridTemplateRows   = "80px";
      const toolbar = document.getElementById("toolbar");
      if (toolbar) toolbar.style.gridTemplateRows = "80px";
      if (!dontToggle) this.ToggleFullscreenForElement(document.getElementById("app"));
    },

    ToggleFullscreenForElement(container) {
      if (document.fullscreenElement) {
        (document.exitFullscreen ||
          document.mozCancelFullScreen ||
          document.webkitCancelFullScreen ||
          document.msExitFullscreen
        )?.call(document);
      } else if (!document.mozFullScreen && !document.webkitFullScreen) {
        (container.requestFullscreen ||
          container.mozRequestFullScreen ||
          container.webkitRequestFullScreen ||
          container.msRequestFullscreen
        )?.call(container);
      }
    },
  },
});
